import { useState } from 'react'

import { api } from '../../api/client'
import type { PatchPolicy, PatchPolicyInput, PatchPolicyRing, PatchPolicyRun } from '../../api/types'
import { useAsync } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import { Card, EmptyState, ErrorBanner, Spinner, formatDateTime } from '../common'
import { DataTable, type Column } from '../DataTable'
import { chipClass, severityLabel, useIsTenantAdmin } from './labels'

function giornoLabel(i: number): string {
  switch (i) {
    case 0: return t('lunedì')
    case 1: return t('martedì')
    case 2: return t('mercoledì')
    case 3: return t('giovedì')
    case 4: return t('venerdì')
    case 5: return t('sabato')
    default: return t('domenica')
  }
}

/**
 * Rollout policies (patch_policy_service): what to install, ring after ring
 * (pilot → test → production), when — approved once by an administrator. Each
 * cycle creates one intervention per ring, approved in their name; the next
 * ring starts only if the previous one succeeded above its threshold.
 */
export function Policies({ onOpenJob }: { onOpenJob: (jobId: string) => void }) {
  const state = useAsync(() => api.patchPolicies(), [])
  const admin = useIsTenantAdmin()
  const [editing, setEditing] = useState<PatchPolicy | 'new' | null>(null)
  const [lavoro, setLavoro] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const [fatto, setFatto] = useState<string | null>(null)

  const esegui = async (azione: () => Promise<unknown>, messaggio: string) => {
    setLavoro(true)
    setErrore(null)
    setFatto(null)
    try {
      await azione()
      setFatto(messaggio)
      state.reload()
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }
  const approva = (p: PatchPolicy) => {
    if (!window.confirm(t('Approvare «{nome}»? Da ora i suoi cicli partono da soli e gli interventi risultano approvati a tuo nome, citando la politica.', { nome: p.name }))) return
    void esegui(() => api.patchPolicyApprove(p.id), t('Politica approvata.'))
  }
  const avvia = (p: PatchPolicy) => {
    if (!window.confirm(t('Avviare adesso un ciclo di «{nome}»? Parte la prima ondata.', { nome: p.name }))) return
    void esegui(() => api.patchPolicyRunNow(p.id), t('Ciclo avviato.'))
  }
  const ferma = (p: PatchPolicy) => {
    if (!p.last_run || !window.confirm(t('Fermare il ciclo di «{nome}»? L’ondata in corso si annulla dove non è ancora partita, le successive non partono.', { nome: p.name }))) return
    void esegui(() => api.patchPolicyRunStop(p.last_run!.id), t('Ciclo fermato.'))
  }
  const eliminaPolitica = (p: PatchPolicy) => {
    if (!window.confirm(t('Eliminare la politica «{nome}» e la storia dei suoi cicli? Gli interventi già creati restano.', { nome: p.name }))) return
    void esegui(() => api.patchPolicyDelete(p.id), t('Politica eliminata.'))
  }

  const colonne: Column<PatchPolicy>[] = [
    {
      key: 'name', header: t('Politica'), value: (p) => p.name,
      render: (p) => (
        <>
          <strong>{p.name}</strong>{!p.enabled && <span className="muted"> · {t('spenta')}</span>}
          <div className="text-muted patch-sub">{cosaLabel(p)}</div>
        </>
      ),
    },
    { key: 'rings', header: t('Ondate'), value: (p) => p.rings.map((r) => r.name).join(' → '),
      render: (p) => <span title={p.rings.map((r) => `${r.name}: ${r.tag} · ${t('attesa {n} giorni', { n: r.delay_days })} · ${t('soglia {n} %', { n: r.min_success })}`).join('\n')}>{p.rings.map((r) => r.name).join(' → ')}</span> },
    { key: 'cadence', header: t('Quando'), value: (p) => cadenzaLabel(p),
      render: (p) => <>{cadenzaLabel(p)}{p.window_name && <div className="text-muted patch-sub">{t('nella finestra «{w}»', { w: p.window_name })}</div>}</> },
    { key: 'next', header: t('Prossimo ciclo'), type: 'date', value: (p) => p.next_cycle_at, render: (p) => formatDateTime(p.next_cycle_at) },
    {
      key: 'approved', header: t('Approvazione'), value: (p) => (p.approved ? t('approvata') : t('da approvare')),
      render: (p) => (p.approved
        ? <span title={t('approvata da {chi} il {quando}', { chi: p.approved_by ?? '', quando: formatDateTime(p.approved_at) })} className={chipClass('ok')}>{t('approvata')}</span>
        : <span className={chipClass('warn')}>{p.changed_since_approval ? t('cambiata: da riapprovare') : t('da approvare')}</span>),
    },
    { key: 'last', header: t('Ultimo ciclo'), value: (p) => (p.last_run ? cicloLabel(p.last_run) : ''), render: (p) => (p.last_run ? <Ciclo run={p.last_run} onOpenJob={onOpenJob} /> : <span className="muted">—</span>) },
    {
      key: 'actions', header: '', value: () => '', sort: false, filter: false,
      render: (p) => admin && (
        <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="link" onClick={() => setEditing(p)}>{t('Modifica')}</button>
          {!p.approved && <button type="button" className="link" disabled={lavoro} onClick={() => approva(p)}>{t('Approva')}</button>}
          {p.approved && p.last_run?.status !== 'running' && <button type="button" className="link" disabled={lavoro} onClick={() => avvia(p)}>{t('Avvia adesso')}</button>}
          {p.last_run?.status === 'running' && <button type="button" className="link" disabled={lavoro} onClick={() => ferma(p)}>{t('Ferma il ciclo')}</button>}
          <button type="button" className="link" disabled={lavoro} onClick={() => eliminaPolitica(p)}>{t('Elimina')}</button>
        </span>
      ),
    },
  ]

  return (
    <Card title={t('Politiche di rilascio')} actions={admin ? <button type="button" onClick={() => setEditing('new')}>{t('Nuova politica')}</button> : undefined}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Cosa installare, su quali macchine e in che ordine (ondate: pilota, poi collaudo, poi produzione), e quando (a ogni Patch Tuesday, ogni settimana, ogni mese). Un amministratore la approva una volta; da lì ogni ciclo crea un intervento per ondata, approvato a suo nome, e l’ondata successiva parte solo se la precedente è riuscita sopra la sua soglia, dopo i giorni di attesa. Se la politica cambia, va riapprovata.')}
      </p>
      <ErrorBanner error={errore ?? state.error} />
      {fatto && <div className="banner banner--ok">{fatto}</div>}
      {!state.data ? !state.error && <Spinner /> : state.data.length === 0
        ? <EmptyState title={t('Nessuna politica di rilascio.')} hint={admin ? t('«Nuova politica» per il primo rilascio automatico: per esempio gli aggiornamenti di sicurezza due giorni dopo il Patch Tuesday, prima ai pilota.') : undefined} />
        : <DataTable rows={state.data} columns={colonne} rowKey={(p) => p.id} conteggio={{ nome: t('politiche') }}
                     expand={(p) => <StoriaCicli policyId={p.id} onOpenJob={onOpenJob} />} />}
      {editing && <Editor politica={editing === 'new' ? null : editing} onClose={() => setEditing(null)}
                          onSaved={(m) => { setEditing(null); setFatto(m); state.reload() }} />}
    </Card>
  )
}

function cosaLabel(p: PatchPolicyInput): string {
  switch (p.kind) {
    case 'security_all': return t('aggiornamenti di sicurezza')
    case 'all_updates': return t('tutti gli aggiornamenti')
    case 'third_party': return t('programmi di terze parti')
    default: {
      const c = p.criteria
      return t('patch {sev} o più gravi{kev}{rischio}', {
        sev: severityLabel(c.min_severity ?? 'alta').toLowerCase(),
        kev: c.kev ? ` · ${t('e tutte le sfruttate (KEV)')}` : '',
        rischio: c.min_risk ? ` · ${t('rischio ≥ {n}', { n: c.min_risk })}` : '',
      })
    }
  }
}

function cadenzaLabel(p: PatchPolicyInput): string {
  const c = p.cadence
  if (c.type === 'weekly') return t('ogni {g} alle {ora}', { g: giornoLabel(c.weekday ?? 2), ora: c.at })
  if (c.type === 'monthly') return t('il giorno {d} di ogni mese alle {ora}', { d: c.day ?? 1, ora: c.at })
  return c.offset_days
    ? t('{n} giorni dopo il Patch Tuesday, alle {ora}', { n: c.offset_days, ora: c.at })
    : t('il Patch Tuesday, alle {ora}', { ora: c.at })
}

function cicloLabel(r: PatchPolicyRun): string {
  if (r.status === 'done') return t('concluso')
  if (r.status === 'stopped') return t('fermato')
  return t('in corso')
}

function anelloLabel(s: string): string {
  switch (s) {
    case 'running': return t('in corso')
    case 'done': return t('riuscita')
    case 'skipped': return t('niente da installare')
    case 'stopped': return t('fermata')
    default: return s
  }
}

function Ciclo({ run, onOpenJob }: { run: PatchPolicyRun; onOpenJob: (jobId: string) => void }) {
  const tono = run.status === 'stopped' ? 'warn' : run.status === 'done' ? 'ok' : 'info'
  return (
    <div>
      <span className={chipClass(tono)}>{cicloLabel(run)}</span>{' '}
      <span className="text-muted">{formatDateTime(run.started_at)}</span>
      <div className="patch-sub">
        {run.rings.map((r, i) => (
          <span key={i} style={{ marginRight: 8 }}>
            {r.job_id ? <button type="button" className="link" onClick={() => onOpenJob(r.job_id!)}>{r.name}</button> : r.name}
            {': '}{anelloLabel(r.status)}{r.success != null && ` ${r.success} %`}
          </span>
        ))}
      </div>
      {run.message && <div className="field__hint" style={{ margin: 0 }}>{run.message}</div>}
    </div>
  )
}

function StoriaCicli({ policyId, onOpenJob }: { policyId: string; onOpenJob: (jobId: string) => void }) {
  const runs = useAsync(() => api.patchPolicyRuns(policyId), [policyId])
  if (runs.error) return <ErrorBanner error={runs.error} />
  if (!runs.data) return <Spinner />
  if (runs.data.length === 0) return <p className="muted">{t('Nessun ciclo ancora.')}</p>
  return (
    <table className="table">
      <thead><tr><th>{t('Inizio')}</th><th>{t('Stato')}</th><th>{t('Ondate')}</th></tr></thead>
      <tbody>
        {runs.data.map((r) => (
          <tr key={r.id}>
            <td>{formatDateTime(r.started_at)}</td>
            <td>{cicloLabel(r)}{r.message && <div className="field__hint" style={{ margin: 0 }}>{r.message}</div>}</td>
            <td>
              {r.rings.map((a, i) => (
                <div key={i}>
                  {a.job_id ? <button type="button" className="link" onClick={() => onOpenJob(a.job_id!)}>{a.name}</button> : a.name}
                  {' · '}{anelloLabel(a.status)}
                  {a.machines ? ` · ${t('{n} macchine', { n: a.machines })}` : ''}
                  {a.success != null && ` · ${t('riuscita {n} %', { n: a.success })}`}
                  {a.failed ? ` · ${t('{n} fallite', { n: a.failed })}` : ''}
                  {a.blocked ? ` · ${t('{n} bloccate', { n: a.blocked })}` : ''}
                  {a.message && <span className="muted"> · {a.message}</span>}
                </div>
              ))}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const VUOTA: PatchPolicyInput = {
  name: '', enabled: true, kind: 'security_all', criteria: { min_severity: 'alta', kev: true, min_risk: 0, kinds: ['kb', 'package', 'software'] },
  rings: [
    { name: 'Pilota', tag: 'ambiente:pilota', delay_days: 0, min_success: 90 },
    { name: 'Collaudo', tag: 'ambiente:collaudo', delay_days: 2, min_success: 90 },
    { name: 'Produzione', tag: 'ambiente:produzione', delay_days: 5, min_success: 95 },
  ],
  cadence: { type: 'patch_tuesday', offset_days: 2, at: '02:00' }, timezone: 'Europe/Rome', window_id: null,
  allow_reboot: false, reboot_delay_min: 10,
}

function Editor({ politica, onClose, onSaved }: { politica: PatchPolicy | null; onClose: () => void; onSaved: (m: string) => void }) {
  const finestre = useAsync(() => api.patchWindows(), [])
  const [v, setV] = useState<PatchPolicyInput>(politica ? {
    name: politica.name, enabled: politica.enabled, kind: politica.kind, criteria: { ...VUOTA.criteria, ...politica.criteria },
    rings: politica.rings, cadence: politica.cadence, timezone: politica.timezone, window_id: politica.window_id,
    allow_reboot: politica.allow_reboot, reboot_delay_min: politica.reboot_delay_min,
  } : VUOTA)
  const [lavoro, setLavoro] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const salva = async () => {
    setLavoro(true)
    setErrore(null)
    try {
      if (politica) await api.patchPolicyUpdate(politica.id, v)
      else await api.patchPolicyCreate(v)
      onSaved(politica && politica.approved ? t('Salvata. Se è cambiato cosa si installa, dove o quando, va riapprovata.') : t('Salvata: ora va approvata.'))
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }
  const anello = (i: number, x: Partial<PatchPolicyRing>) => setV({ ...v, rings: v.rings.map((r, j) => (j === i ? { ...r, ...x } : r)) })
  const tipi = v.criteria.kinds ?? []
  return (
    <div className="dob-modal" role="dialog" aria-modal="true" aria-label={t('Politica di rilascio')}>
      <div className="dob-modal__scrim" onClick={onClose} />
      <div className="dob-modal__pannello">
        <div className="dob-modal__testata">
          <h3 className="dob-modal__titolo">{politica ? politica.name : t('Nuova politica di rilascio')}</h3>
          <button type="button" className="secondary" onClick={onClose}>{t('Chiudi')}</button>
        </div>
        <ErrorBanner error={errore} />
        <form className="form-dialog" onSubmit={(e) => { e.preventDefault(); void salva() }}>
          <h4 className="form__sezione">{t('Cosa')}</h4>
          <div className="form-grid">
            <label className="field span-2"><span className="field__label">{t('Nome')}</span>
              <input type="text" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} required maxLength={200} /></label>
            <label className="field span-2"><span className="field__label">{t('Da installare')}</span>
              <select value={v.kind} onChange={(e) => setV({ ...v, kind: e.target.value as PatchPolicyInput['kind'] })}>
                <option value="security_all">{t('aggiornamenti di sicurezza')}</option>
                <option value="all_updates">{t('tutti gli aggiornamenti')}</option>
                <option value="third_party">{t('programmi di terze parti')}</option>
                <option value="criteria">{t('le patch che rispondono ai criteri')}</option>
              </select></label>
            {v.kind === 'criteria' && (
              <>
                <label className="field span-1"><span className="field__label">{t('Gravità minima')}</span>
                  <select value={v.criteria.min_severity} onChange={(e) => setV({ ...v, criteria: { ...v.criteria, min_severity: e.target.value } })}>
                    {['critica', 'alta', 'media', 'bassa'].map((s) => <option key={s} value={s}>{severityLabel(s)}</option>)}
                  </select></label>
                <label className="field span-1"><span className="field__label">{t('Rischio minimo (0–100)')}</span>
                  <input type="number" min={0} max={100} value={v.criteria.min_risk ?? 0} onChange={(e) => setV({ ...v, criteria: { ...v.criteria, min_risk: Number(e.target.value) } })} /></label>
                <label className="field span-2" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={Boolean(v.criteria.kev)} onChange={(e) => setV({ ...v, criteria: { ...v.criteria, kev: e.target.checked } })} />
                  <span>{t('Anche le sfruttate (CISA KEV), qualunque gravità')}</span></label>
                <div className="field span-4"><span className="field__label">{t('Tipi di patch')}</span>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {[{ k: 'kb', l: t('KB di Windows') }, { k: 'package', l: t('pacchetti del sistema') }, { k: 'software', l: t('programmi installati') }].map((x) => (
                      <label key={x.k} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        <input type="checkbox" checked={tipi.includes(x.k)}
                               onChange={(e) => setV({ ...v, criteria: { ...v.criteria, kinds: e.target.checked ? [...tipi, x.k] : tipi.filter((y) => y !== x.k) } })} />
                        {x.l}
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          <h4 className="form__sezione">{t('Dove, in che ordine')}</h4>
          <p className="field__hint" style={{ marginTop: 0 }}>{t('Ogni ondata prende le macchine con il suo tag. L’ondata successiva parte dopo i giorni di attesa, solo se questa è riuscita almeno per la soglia (le macchine bloccate — senza agente, senza tag d’ambiente — non contano).')}</p>
          {v.rings.map((r, i) => (
            <div key={i} className="form-grid">
              <label className="field span-1"><span className="field__label">{t('Ondata')}</span>
                <input type="text" value={r.name} onChange={(e) => anello(i, { name: e.target.value })} required maxLength={80} /></label>
              <label className="field span-2"><span className="field__label">{t('Tag delle macchine')}</span>
                <input type="text" value={r.tag} onChange={(e) => anello(i, { tag: e.target.value })} placeholder={'ambiente:collaudo'} required /></label>
              <label className="field span-1"><span className="field__label">{i === 0 ? t('Attesa (giorni)') : t('Attesa dopo la precedente (giorni)')}</span>
                <input type="number" min={0} max={60} value={r.delay_days} disabled={i === 0} onChange={(e) => anello(i, { delay_days: Number(e.target.value) })} /></label>
              <label className="field span-1"><span className="field__label">{t('Soglia di riuscita (%)')}</span>
                <input type="number" min={0} max={100} value={r.min_success} onChange={(e) => anello(i, { min_success: Number(e.target.value) })} /></label>
              <div className="field span-1" style={{ alignSelf: 'end' }}>
                {v.rings.length > 1 && <button type="button" className="link" onClick={() => setV({ ...v, rings: v.rings.filter((_, j) => j !== i) })}>{t('togli')}</button>}
              </div>
            </div>
          ))}
          {v.rings.length < 6 && (
            <button type="button" className="secondary" onClick={() => setV({ ...v, rings: [...v.rings, { name: t('Ondata {n}', { n: v.rings.length + 1 }), tag: '', delay_days: 1, min_success: 90 }] })}>{t('Aggiungi un’ondata')}</button>
          )}

          <h4 className="form__sezione">{t('Quando')}</h4>
          <div className="form-grid">
            <label className="field span-2"><span className="field__label">{t('Cadenza')}</span>
              <select value={v.cadence.type} onChange={(e) => setV({ ...v, cadence: { ...v.cadence, type: e.target.value as PatchPolicyInput['cadence']['type'] } })}>
                <option value="patch_tuesday">{t('dopo il Patch Tuesday (secondo martedì del mese)')}</option>
                <option value="weekly">{t('ogni settimana')}</option>
                <option value="monthly">{t('ogni mese')}</option>
              </select></label>
            {v.cadence.type === 'patch_tuesday' && (
              <label className="field span-1"><span className="field__label">{t('Giorni dopo')}</span>
                <input type="number" min={0} max={27} value={v.cadence.offset_days ?? 0} onChange={(e) => setV({ ...v, cadence: { ...v.cadence, offset_days: Number(e.target.value) } })} /></label>
            )}
            {v.cadence.type === 'weekly' && (
              <label className="field span-1"><span className="field__label">{t('Giorno')}</span>
                <select value={v.cadence.weekday ?? 2} onChange={(e) => setV({ ...v, cadence: { ...v.cadence, weekday: Number(e.target.value) } })}>
                  {[0, 1, 2, 3, 4, 5, 6].map((i) => <option key={i} value={i}>{giornoLabel(i)}</option>)}
                </select></label>
            )}
            {v.cadence.type === 'monthly' && (
              <label className="field span-1"><span className="field__label">{t('Giorno del mese')}</span>
                <input type="number" min={1} max={28} value={v.cadence.day ?? 1} onChange={(e) => setV({ ...v, cadence: { ...v.cadence, day: Number(e.target.value) } })} /></label>
            )}
            <label className="field span-1"><span className="field__label">{t('Alle')}</span>
              <input type="time" value={v.cadence.at} onChange={(e) => setV({ ...v, cadence: { ...v.cadence, at: e.target.value } })} required /></label>
            <label className="field span-2"><span className="field__label">{t('Finestra di manutenzione')}</span>
              <select value={v.window_id ?? ''} onChange={(e) => setV({ ...v, window_id: e.target.value || null })}>
                <option value="">{t('nessuna: si installa all’avvio dell’ondata')}</option>
                {(finestre.data ?? []).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select></label>
            <label className="field span-2"><span className="field__label">{t('Fuso orario')}</span>
              <input type="text" value={v.timezone} onChange={(e) => setV({ ...v, timezone: e.target.value })} required /></label>
            <label className="field span-2" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={v.allow_reboot} onChange={(e) => setV({ ...v, allow_reboot: e.target.checked })} />
              <span>{t('Riavvia le macchine se serve')}</span></label>
            {v.allow_reboot && (
              <label className="field span-1"><span className="field__label">{t('Preavviso (minuti)')}</span>
                <input type="number" min={1} max={240} value={v.reboot_delay_min} onChange={(e) => setV({ ...v, reboot_delay_min: Number(e.target.value) })} /></label>
            )}
            <label className="field span-2" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={v.enabled} onChange={(e) => setV({ ...v, enabled: e.target.checked })} />
              <span>{t('Accesa')}</span></label>
          </div>
          <div className="form-dialog__azioni">
            <button type="submit" disabled={lavoro}>{lavoro ? t('Salvo…') : t('Salva')}</button>
          </div>
        </form>
      </div>
    </div>
  )
}
